import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import {
  IPermission,
  PermissionsEditor,
} from '../../../../components/PermissionsEditor';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  BackLink,
  Card,
  Container,
  ContainerAvatar,
  Form,
  Header,
  HeaderInfo,
  Save,
  StudentName,
  Subtitle,
} from './styles';

interface IUserProps {
  fullname: string;
  avatar_url?: string;
  permissions: string[];
}

const UserPermissions: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<IUserProps>();
  const [catalog, setCatalog] = useState<IPermission[]>([]);

  const getUserData = useCallback(() => {
    api
      .get(`users/gym/${id}`)
      .then(response => setUser(response.data.user))
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do usuário.',
          description: err?.response?.status,
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, id]);

  const getCatalog = useCallback(() => {
    api
      .get<IPermission[]>('users/permissions/catalog')
      .then(response =>
        setCatalog(
          response.data.filter(permission => permission.scope === 'gym'),
        ),
      )
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao buscar catálogo de permissões.',
        }),
      );
  }, [addToast]);

  useEffect(() => {
    getUserData();
    getCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (data: Record<string, boolean>) => {
      if (!id) return;

      try {
        setSaving(true);

        const selectedCodes = catalog
          .filter(permission => data[permission.code])
          .map(permission => permission.code);

        await api.put(`users/permissions/${id}`, {
          permissions: selectedCodes,
        });

        addToast({
          type: 'success',
          title: 'Alterações realizadas com sucesso.',
        });

        setUser(current =>
          current ? { ...current, permissions: selectedCodes } : current,
        );
      } catch (err: any) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Houve um erro. Tente novamente.',
        });
      } finally {
        setSaving(false);
      }
    },
    [addToast, catalog, id],
  );

  const initialData = catalog.reduce<Record<string, boolean>>(
    (acc, permission) => {
      acc[permission.code] =
        user?.permissions?.includes(permission.code) ?? false;
      return acc;
    },
    {},
  );

  return (
    <Painel loading={loading}>
      <Container>
        <BackLink type="button" onClick={() => navigate(`/users/${id}`)}>
          <Icon name="arrow_back" size={16} color={colors.primary} />
          Detalhes do aluno
        </BackLink>

        <Header>
          <HeaderInfo>
            <ContainerAvatar>
              <img src={user?.avatar_url || noAvatar} alt="avatar do aluno" />
            </ContainerAvatar>
            <div>
              <StudentName>
                Permissões de <span>{formatName(user?.fullname || '')}</span>
              </StudentName>
              <Subtitle>Controle de acesso do usuário na academia</Subtitle>
            </div>
          </HeaderInfo>

          <Save
            type="button"
            disabled={saving}
            onClick={() => formRef.current?.submitForm()}
          >
            Salvar Permissões
          </Save>
        </Header>

        <Form
          key={`${catalog.length}-${user ? 'loaded' : 'pending'}`}
          ref={formRef}
          initialData={initialData}
          onSubmit={handleSubmit}
        >
          <Card>
            <PermissionsEditor
              catalog={catalog}
              formRef={formRef}
              currentPermissions={user?.permissions}
            />
          </Card>
        </Form>
      </Container>
    </Painel>
  );
};

export { UserPermissions };
